import Objdict from "objdict"
import {nanoid} from "nanoid"
import GameAnalytics from "not-game-analytics"

import Stage from "@frog/source/models/Stage.js"
import Step from "@frog/source/models/Step.js"
import World from "@frog/source/models/World.js"
import Zone from "@frog/source/models/Zone.js"
import Geometry from "@frog/source/utility/Geometry.js"
import Elements from "@frog/source/data/elements.js"
import Tiled from "@frog/source/utility/integration/Tiled.js"
import * as Motions from "@frog/source/juice/utility/Motions.js"

import * as PlotFlourishes from "@frog/source/data/plot/flourishes.js"
import App from "@frog/source/models/App.js"
import Timings from "@frog/source/juice/utility/Timings.js"

function canTarget({performerKey, stage, position}) {
    const performer = stage.units[performerKey]
    const target = Stage.findUnitByPosition(stage, position)
    if(target == undefined) return false
    if(target.key == performer.key) return false
    return true
}

function canTargetAlly({performerKey, stage, position}) {
    const performer = stage.units[performerKey]
    const target = Stage.findUnitByPosition(stage, position)
    if(target == undefined) return false
    if(target.affiliation != performer.affiliation) return false
    if(target.key == performer.key) return false
    return true
}

function getSupportPriority({performerKey, stage, position}) {
    const performer = stage.units[performerKey]
    const target = Stage.findUnitByPosition(stage, position)
    if(target != undefined
    && target.affiliation == performer.affiliation) {
        return 5
    } else {
        return 4
    }
}

const skills = {}

skills["inflict-damage"] = {
    "key": "inflict-damage",
    "name": "Attack",
    "role": "damage",

    "type": "invoke",
    "range": null, // will default to the player's attackrange.

    "canBeCounterattacked": true,
    "canCounterattack": ({performerKey, stage, position}) => {
        const performer = stage.units[performerKey]
        if(performer?.ai?.isNotCounterattacking == true) return false
        return true
    },

    "isRenderedEvenIfCanNotTarget": true,

    "power": 0, // will default to the player's power.
    "isIgnoringArmor": false,
    "isIgnoringElements": false,

    // "isHidden": true,

    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation == "none") return false
        if(target.affiliation == "nothing") return false
        if(target.type == "thing") return false
        if(performer.canBetray != true && target.affiliation == performer.affiliation) return false
        return true
    },
    "getPriority": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return 0
        if(target.affiliation != performer.affiliation) return 5
        if(target.properties?.includes("can be pacified")) return 5
        return 4
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const steps = []

        if(stage.units[performerKey].dialogue != undefined
        && stage.units[performerKey].dialogue["attack"] != undefined) {
            steps.push(...Stage.processEventSteps(stage, stage.units[performerKey].dialogue["attack"]))
        }

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const distance = Geometry.distance(position, performer.position)

        let target = Stage.findUnitByPosition(stage, position)

        stage.conditions.forEach((condition) => {
            if(condition.type == "before attacking"
            && condition.canBeTriggered(stage, performer, target) == true) {
                let events = undefined
                if(condition.events instanceof Function) {
                    events = condition.events(stage, performer, target)
                } else {
                    events = condition.events
                }
                events.push({
                    "name": "SavestateCapture",
                    "savestateSlug": condition.title || "Condition_" + stage.conditions.indexOf(condition),
                })
                steps.push(...Stage.processEventSteps(stage, events))

                if(steps[steps.length - 1] != undefined
                && steps[steps.length - 1].omegastage != undefined) {
                    stage = steps[steps.length - 1].omegastage
                }
            }
        })

        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        step.followupSteps = []
        steps.push(step)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 180, "audio": {"triggerKey": "Unit:Attack:Melee"}})

        if(this.canTarget({performerKey, stage, position}) != true) {
            return steps
        }

        target = Stage.findUnitByPosition(step.omegastage, position)
        if(target == undefined) {
            return steps
        }

        /////////////
        // DAMAGE //
        ///////////

        const range = (this.range && this.range.max) || (performer.equippedItem?.attackrange && performer.equippedItem?.attackrange?.max) || (performer.attackrange && performer.attackrange.max) || 1
        let hitmark = 200 + ((distance - 1) * 50)

        let innatePower = (performer.power || 0) + (performer.equippedItem?.power || 0)

        if(performer.properties != undefined
        && performer.properties.includes("counter")) {
            if(performer.affiliation == stage.player.affiliation && stage.turn.number % 2 != 0
            || performer.affiliation != stage.player.affiliation && stage.turn.number % 2 == 0) {
                innatePower += 20
            }
        }

        let damage = innatePower

        if(damage == 0) {
            return steps
        }

        const attacktype = performer.equippedItem?.attacktype || performer.attacktype

        if(attacktype == "steel") {
            damage -= target.defense || 0
        }
        if(attacktype == "magic") {
            damage -= target.resistance || 0
        }

        const attackElement = performer.equippedItem?.element || performer.element || performer.attackelement
        const defendElement = target.equippedItem?.element || target.element || target.attack?.element
        let isSuperEffective = false
        let isIneffective = false
        if(attackElement != undefined) {
            if(Elements[defendElement]?.weaknesses != undefined
            && Elements[defendElement].weaknesses.includes(attackElement)) {
                // damage += Math.ceil(damage * (1 / 2))
                isSuperEffective = true
            }
            if(Elements[defendElement]?.strengths != undefined
            && Elements[defendElement].strengths.includes(attackElement)) {
                // Decrease the damage by a quarter...
                let damageReduction = damage / 4

                // ...rounded to the nearest 5...
                damageReduction = Math.floor(damageReduction/ 5) * 5

                // ...with a minimum of 5.
                damageReduction = Math.max(5, damageReduction)

                damage -= damageReduction
                isIneffective = true
            }
        }

        // PERFORMING ANIMATION HERE //

        if(damage == 0) {
            Step.add(step, {
                "mark": hitmark,
                "audio": {"triggerKey": "Unit:Attack:NoDamage"}
            })
        }
        if(damage > 0) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": target.key,
                    "health": Math.min(target.maxhealth, Math.max(0, target.health - damage)),
                },
            })

            if(isSuperEffective != true
            && performer?.properties?.includes("looting")) {
                if(target.health <= 0) {
                    // TODO: Add modal to inform about the new weapon
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": performer.key,
                            "element": defendElement,
                            "weaponImageKey": target.weaponImageKey,
                            "attacktype": target.attacktype,
                            "attackrange": target.attackrange,
                            "power": target.power,
                        }
                    })
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": target.key,
                            "element": "undefined",
                            "weaponImageKey": "",
                            "attacktype": "none",
                            "attackrange": 1,
                            "power": 10,
                        }
                    })
                }
            }

            if(isSuperEffective != true
            && performer.properties != undefined
            && performer.properties.includes("forceful")) {
                const direction = Geometry.direction(position, performer.position)
                const vector = Geometry.vector(position, performer.position)

                const previousPosition = position
                const nextPosition = Geometry.add(position, vector)

                const collision = Stage.findCollision({
                    stage, previousPosition, nextPosition,
                })

                if(collision == undefined) {
                    Step.add(step, PlotFlourishes.PushedMovement({
                        "mark": hitmark,
                        "unitKey": target.key,
                        "startPosition": previousPosition,
                        "endPosition": nextPosition,
                    }))
                    // Step.add(step, {
                    //     "mark": hitmark,
                    //     "unit": {
                    //         "key": target.key,
                    //         "position": nextPosition,
                    //     }
                    // })
                } else if(collision != undefined) {
                    Step.add(step, PlotFlourishes.NudgedMovement({
                        "mark": hitmark,
                        "unitKey": target.key,
                        "position": previousPosition,
                        "direction": direction,
                    }))

                    const damage = 1
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": target.key,
                            "health": target.health - damage
                        },
                    })

                    if(collision.unit != undefined) {
                        Step.add(step, {
                            "mark": hitmark,
                            "unit": {
                                "key": collision.unit.key,
                                "health": collision.unit.health - damage
                            },
                        })
                    }
                }
            } else {
                Step.add(step, PlotFlourishes.NudgedMovement({
                    "mark": hitmark,
                    "unitKey": target.key,
                    "position": position,
                    "direction": direction,
                }))
            }

            Step.add(step, {
                "mark": hitmark,
                "audio": {"triggerKey": target.health < 0 ? "Unit:Attack:FinalHit" : "Unit:Attack:Hit"}
            })
            Step.add(step, {"mark": hitmark, "audio": {"triggerKey": performer.equippedItem?.weaponAudioKey || performer.weaponAudioKey}})

            if(performer.properties != undefined
            && performer.properties.includes("lifesteal")) {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": performer.key,
                        "health": Math.min(performer.maxhealth, Math.max(0, performer.health + 5)),
                    },
                })
                Step.add(step, PlotFlourishes.TextFloat({
                    "mark": hitmark,
                    "type": "healing",
                    "unitKey": performer.key,
                    "position": performer.position,
                    "text": "+5",
                }))
            }
        }
        Step.add(step, PlotFlourishes.TextFloat({
            "mark": hitmark,
            "type": "damage",
            "unitKey": target.key,
            "position": target.position,
            "text": (damage > 0 ? "-" : "") + damage,
        }))

        if(isSuperEffective) {
            damage -= Math.ceil(damage * (1 / 2))
            hitmark *= 2
            hitmark += 50

            Step.add(step, PlotFlourishes.SwingBackMovement({
                "mark": hitmark - 133,
                "unitKey": performer.key,
                "position": performer.position,
                "direction": direction,
            }))
            Step.add(step, {
                "mark": hitmark + 180,
                "audio": {"triggerKey": "Unit:Attack:Melee"}
            })
            Step.add(step, {
                "mark": hitmark + 180,
                "audio": {"triggerKey": "Unit:Attack:" + (performer.equippedItem?.element || performer.element)}
            })

            if(damage == 0) {
                Step.add(step, {
                    "mark": hitmark,
                    "audio": {"triggerKey": "Unit:Attack:NoDamage"}
                })
            }
            if(damage > 0) {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": target.key,
                        "health": Math.min(target.maxhealth, Math.max(0, target.health - damage)),
                    },
                })

                if(performer?.properties?.includes("looting")) {
                    if(target.health <= 0) {
                        // TODO: Add modal to inform about the new weapon
                        Step.add(step, {
                            "mark": hitmark,
                            "unit": {
                                "key": performer.key,
                                "element": defendElement,
                                "weaponImageKey": target.weaponImageKey,
                                "attacktype": target.attacktype,
                                "attackrange": target.attackrange,
                                "power": target.power,
                            }
                        })
                        Step.add(step, {
                            "mark": hitmark,
                            "unit": {
                                "key": target.key,
                                "element": "undefined",
                                "weaponImageKey": "none",
                                "attacktype": "none",
                                "attackrange": 1,
                                "power": 10,
                            }
                        })
                    }
                }

                if(performer.properties != undefined
                && performer.properties.includes("forceful")) {
                    const direction = Geometry.direction(position, performer.position)
                    const vector = Geometry.vector(position, performer.position)

                    const previousPosition = position
                    const nextPosition = Geometry.add(position, vector)

                    const collision = Stage.findCollision({
                        stage, previousPosition, nextPosition,
                    })

                    if(collision == undefined) {
                        Step.add(step, PlotFlourishes.PushedMovement({
                            "mark": hitmark,
                            "unitKey": target.key,
                            "startPosition": previousPosition,
                            "endPosition": nextPosition,
                        }))
                        // Step.add(step, {
                        //     "mark": hitmark,
                        //     "unit": {
                        //         "key": target.key,
                        //         "position": nextPosition,
                        //     }
                        // })
                    } else if(collision != undefined) {
                        Step.add(step, PlotFlourishes.NudgedMovement({
                            "mark": hitmark,
                            "unitKey": target.key,
                            "position": target.position,
                            "direction": direction,
                        }))

                        const damage = 1
                        Step.add(step, {
                            "mark": hitmark,
                            "unit": {
                                "key": target.key,
                                "health": target.health - damage
                            },
                        })

                        if(collision.unit != undefined) {
                            Step.add(step, {
                                "mark": hitmark,
                                "unit": {
                                    "key": collision.unit.key,
                                    "health": collision.unit.health - damage
                                },
                            })
                        }
                    }
                } else {
                    Step.add(step, PlotFlourishes.NudgedMovement({
                        "mark": hitmark,
                        "unitKey": target.key,
                        "position": target.position,
                        "direction": direction,
                    }))
                }

                // Step.add(step, PlotFlourishes.NudgedMovement({
                //     "mark": hitmark,
                //     "unitKey": target.key,
                //     "position": target.position,
                //     "direction": direction,
                // }))
                Step.add(step, {
                    "mark": hitmark,
                    "audio": {"triggerKey": target.health < 0 ? "Unit:Attack:FinalHit" : "Unit:Attack:Hit"}
                })
                Step.add(step, {"mark": hitmark, "audio": {"triggerKey": performer.equippedItem?.weaponAudioKey || performer.weaponAudioKey}})

                if(performer.properties != undefined
                && performer.properties.includes("lifesteal")) {
                    Step.add(step, {
                        "mark": hitmark,
                        "unit": {
                            "key": performer.key,
                            "health": Math.min(performer.maxhealth, Math.max(0, performer.health + 10)),
                        },
                    })
                    Step.add(step, PlotFlourishes.TextFloat({
                        "mark": hitmark,
                        "type": "healing",
                        "unitKey": performer.key,
                        "position": performer.position,
                        "text": "+5",
                    }))
                }
            }
            Step.add(step, PlotFlourishes.TextFloat({
                "mark": hitmark,
                "type": "damage",
                "unitKey": target.key,
                "position": target.position,
                "text": (damage > 0 ? "-" : "") + damage,
            }))

            Step.add(step, PlotFlourishes.TextFloat({
                "mark": hitmark,
                "type": "damage",
                "unitKey": target.key,
                "position": {
                    "x": target.position.x,
                    "y": target.position.y + 0.2,
                },
                "text": "SUPER",
            }))

            Step.add(step, PlotFlourishes.TextFloat({
                "mark": hitmark,
                "type": "damage",
                "unitKey": target.key,
                "position": {
                    "x": target.position.x,
                    "y": target.position.y + 0.4,
                },
                "text": "EFFECTIVE",
            }))
        }

        if(isIneffective) {
            Step.add(step, PlotFlourishes.TextFloat({
                "mark": hitmark,
                "type": "damage",
                "unitKey": target.key,
                "position": {
                    "x": target.position.x,
                    "y": target.position.y + 0.2,
                },
                "text": "INEFFECTIVE",
            }))
        }

        const encounter = Object.values(stage.world.zones).find((zone) => {
            return Zone.isInBounds(zone, target.position)
        })

        Step.add(step, {
            "mark": hitmark,
            "unit": {
                "key": target.key,
                "state": "aggroed",
            }
        })

        // Object.values(stage.units).forEach((unit) => {
        //     if(unit.affiliation == "bad"
        //     && Zone.isInBounds(encounter, unit.position)) {
        //         Step.add(step, {
        //             "mark": hitmark,
        //             "unit": {
        //                 "key": unit.key,
        //                 "state": "aggroed",
        //             }
        //         })
        //     }
        // })

        if(performer.affiliation == stage.player.affiliation) {
            if(stage.flags["tutorial:hasAttacked"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttacked", true)})
            }
            if(isIneffective == true
            && stage.flags["tutorial:hasAttackedIneffectively"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedIneffectively", true)})
            }
            if((attackElement == "earth" || attackElement == "axe" || attackElement == "gun")
            && (defendElement == "water" || defendElement == "spear" || defendElement == "bow")
            && stage.flags["tutorial:hasAttackedEffectivelyWithEarth"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedIneffectivelyWithEarth", true)})
            }
            if(target.attackrange != performer.attackrange
            && stage.flags["tutorial:hasAttackedWithoutCounterattack"] != true) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedWithoutCounterattack", true)})
            }
            if((attackElement == "earth" || attackElement == "axe" || attackElement == "gun")
            && (defendElement == "earth" || defendElement == "axe" || defendElement == "gun")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedBlueWithBlue", true)})
            }
            if((attackElement == "water" || attackElement == "spear" || attackElement == "bow")
            && (defendElement == "water" || defendElement == "spear" || defendElement == "bow")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedGreenWithGreen", true)})
            }
            if((attackElement == "fire" || attackElement == "sword" || attackElement == "dagger")
            && (defendElement == "fire" || defendElement == "sword" || defendElement == "dagger")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasAttackedRedWithRed", true)})
            }
            if((attackElement == "earth" || attackElement == "axe" || attackElement == "gun")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerColor", "green")})
            }
            if((attackElement == "water" || attackElement == "spear" || attackElement == "bow")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerColor", "blue")})
            }
            if((attackElement == "fire" || attackElement == "sword" || attackElement == "dagger")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackerColor", "red")})
            }
            if((defendElement == "earth" || defendElement == "axe" || defendElement == "gun")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedColor", "green")})
            }
            if((defendElement == "water" || defendElement == "spear" || defendElement == "bow")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedColor", "blue")})
            }
            if((defendElement == "fire" || defendElement == "sword" || defendElement == "dagger")) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:AttackedColor", "red")})
            }

        } else {
            Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasBeenAttacked", true)})
            if(stage.turn.number % 2 == 0) {
                Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasBeenAttackedInCounterattack", true)})
            }
        }

        Step.add(step, {
            "mark": 0,
            "func": () => {
                if(performer.affiliation == stage.player.affiliation) {
                    GameAnalytics.reportDesignEvent([
                        "chapter-" + stage.chapterSlug,
                        "encounter-" + stage.currentEncounterKey,
                        "player-attacked"
                    ].join(":"))
                    if(isSuperEffective) {
                        GameAnalytics.reportDesignEvent([
                            "chapter-" + stage.chapterSlug,
                            "encounter-" + stage.currentEncounterKey,
                            "player-attacked-effectively",
                        ].join(":"))
                    }
                    if(isIneffective) {
                        GameAnalytics.reportDesignEvent([
                            "chapter-" + stage.chapterSlug,
                            "encounter-" + stage.currentEncounterKey,
                            "player-attacked-ineffectively",
                        ].join(":"))
                    }
                    if(target.health == 0) {
                        GameAnalytics.reportDesignEvent([
                            "chapter-" + stage.chapterSlug,
                            "encounter-" + stage.currentEncounterKey,
                            "player-killed"
                        ].join(":"))
                    }
                }
                if(target.affiliation == stage.player.affiliation) {
                    GameAnalytics.reportDesignEvent([
                        "chapter-" + stage.chapterSlug,
                        "encounter-" + stage.currentEncounterKey,
                        "player-was-attacked"
                    ].join(":"))
                    if(isSuperEffective) {
                        GameAnalytics.reportDesignEvent([
                            "chapter-" + stage.chapterSlug,
                            "encounter-" + stage.currentEncounterKey,
                            "player-was-attacked-effectively",
                        ].join(":"))
                    }
                    if(isIneffective) {
                        GameAnalytics.reportDesignEvent([
                            "chapter-" + stage.chapterSlug,
                            "encounter-" + stage.currentEncounterKey,
                            "player-was-attacked-ineffectively",
                        ].join(":"))
                    }
                    if(target.health == 0) {
                        GameAnalytics.reportDesignEvent([
                            "chapter-" + stage.chapterSlug,
                            "encounter-" + stage.currentEncounterKey,
                            "player-was-killed"
                        ].join(":"))
                    }
                }
            }
        })

        if(performer.canBetray == true) {
            if(performer.affiliation == stage.player.affiliation
            && target.affiliation == stage.player.affiliation) {
                Step.add(step, {
                    "mark": hitmark,
                    "unit": {
                        "key": target.key,
                        "affiliation": "neutral",
                        "bodyKey": "neutral",
                    }
                })
                if(target.dialogue != undefined
                && target.dialogue["betrayed"] != undefined
                && target.health > 0) {
                    steps.push(...Stage.processEventSteps(step.omegastage, target.dialogue["betrayed"]))
                }
            }
        }

        stage.conditions.forEach((condition) => {
            if(condition.type == "attacking"
            && condition.canBeTriggered(stage, performer, target) == true) {
                const conditionEvents = condition.events
                conditionEvents.push({
                    "name": "SavestateCapture",
                    "savestateSlug": condition.title || "Condition_" + stage.conditions.indexOf(condition),
                })
                steps.push(...Stage.processEventSteps(step.omegastage, conditionEvents))
            }
        })

        return steps
    }
}

skills["restore-health"] = {
    "key": "restore-health",
    "name": "Heal",
    "role": "support",

    "description": "Heal: Can restore health of an ally.",

    "type": "invoke",
    "range": null,

    "power": 15,

    "getPriority": getSupportPriority,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.key == performer.key) return false
        if(target.affiliation != performer.affiliation
        && target.properties?.includes("can be pacified") == false) {
            return false
        }
        return true
    },

    "calculateStep": function({performerKey, effectKey, stage, position}) {
        const steps = []

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        const hitmark = 0

        // if(target.maxhealth != target.health
        // && performer.dialogue["help"] != undefined) {
        //     steps.push(...Stage.processEventSteps(stage, performer.dialogue["help"]))
        // }

        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        steps.push(step)

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": hitmark,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Heal"}})

        let healing = this.power // + (performer.power || 0)
        // if(target.health + healing > target.maxhealth) {
        //     healing = target.maxhealth - target.health
        // }

        if(target.properties?.includes("can be pacified")) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": target.key,
                    "state": "pacified",
                }
            })
        }

        Step.add(step, {
            "mark": hitmark,
            "unit": {
                "key": target.key,
                "health": Math.min(target.maxhealth, target.health + healing)
            },
        })

        if(performer.properties != undefined
        && performer.properties.includes("mutually-healing")) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": performer.key,
                    "health": Math.min(performer.maxhealth, performer.health + healing)
                },
            })
        }

        if(target.effects.burning != undefined) {
            Step.add(step, {
                "mark": hitmark,
                "unit": {
                    "key": target.key,
                    "effects": {
                        "burning": undefined,
                    }
                },
            })
        }

        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": hitmark,
            "unitKey": target.key,
            "position": target.position,
            "direction": Geometry.flip(direction),
        }))
        Step.add(step, PlotFlourishes.TextFloat({
            "mark": hitmark,
            "type": "healing",
            "unitKey": target.key,
            "position": target.position,
            "text": "+" + healing
        }))

        Step.add(step, {"mark": hitmark, ...Step.deltaflag("tutorial:hasHealed", true)})

        if(target.maxhealth != target.health
        && performer.dialogue != undefined
        && performer.dialogue["help"] != undefined) {
            steps.push(...Stage.processEventSteps(step.omegastage, performer.dialogue["help"]))
        }

        if(target.maxhealth != target.health) {
            Step.add(step, {
                "mark": 0,
                "func": () => {
                    GameAnalytics.reportDesignEvent([
                        "chapter-" + stage.chapterSlug,
                        "encounter-" + stage.currentEncounterKey,
                        "player-healed"
                    ].join(":"))
                }
            })
        }

        return steps
    }
}

// interact?
skills["recruit"] = {
    "key": "recruit",
    "name": "Recruit",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "isHidden": true,

    "getPriority": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return 0
        if(target.type == "thing") return 6
        if(target.affiliation == "none") return 6
        return 0
    },

    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.type == "thing") return true
        if(target.affiliation != "none") return false
        if(target.affiliation != "nothing") return false
        return true
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })
        step.followupSteps = []

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const distance = Geometry.distance(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 180, "audio": {"triggerKey": "Unit:Attack:Melee"}})

        const target = Stage.findUnitByPosition(step.omegastage, position)

        if(target == undefined) {
            return step
        }

        if(target.type == "thing") {
            if(target.jobKey?.includes("chest")
            && target.health < target.maxhealth) {
                Step.add(step, {"mark": 200, ...Step.deltaflag("tutorial:hasDug", true)})
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": target.key,
                        "health": target.health + 10,
                        "imageKey": "chest" + Math.floor((target.health + 10) / 10)
                    }
                })
                if(target.health >= target.maxhealth) {
                    stage.conditions.forEach((condition) => {
                        if(condition.type == "recruiting"
                        && condition.canBeTriggered(stage, performer, target) == true) {
                            const conditionEvents = condition.events
                            conditionEvents.push({
                                "name": "SavestateCapture",
                                "savestateSlug": condition.title || "Condition_" + stage.conditions.indexOf(condition),
                            })
                            step.followupSteps.push(...Stage.processEventSteps(step.omegastage, conditionEvents))
                        }
                    })
                }
            }
        }

        if(target.affiliation == "none") {
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "affiliation": performer.affiliation,
                }
            })

            stage.conditions.forEach((condition) => {
                if(condition.type == "recruiting"
                && condition.canBeTriggered(stage, performer, target) == true) {
                    const conditionEvents = condition.events
                    conditionEvents.push({
                        "name": "SavestateCapture",
                        "savestateSlug": condition.title || "Condition_" + stage.conditions.indexOf(condition),
                    })
                    step.followupSteps.push(...Stage.processEventSteps(step.omegastage, conditionEvents))
                }
            })
        }

        return step
    }
}

// gift vs trade vs buy?
skills["gift"] = {
    "key": "gift",
    "name": "Gift",
    "role": "support",

    "type": "invoke",
    "range": 1,

    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation != performer.affiliation) return false
        if(performer.weaponImageKey == undefined
        || performer.weaponImageKey == "") return false
        return true
    },

    "calculateStep": function({stage, position, performerKey, effectKey}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = step.omegastage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const distance = Geometry.distance(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 180, "audio": {"triggerKey": "Unit:Attack:Melee"}})

        const target = Stage.findUnitByPosition(step.omegastage, position)

        if(target == undefined) {
            return step
        }

        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "element": attackElement,
                "weaponImageKey": performer.weaponImageKey,
                "attacktype": performer.attacktype,
                "attackrange": performer.attackrange,
                "power": performer.power,
            }
        })
        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": performer.key,
                "element": "undefined",
                "weaponImageKey": "",
                "attacktype": "none",
                "attackrange": 1,
                "power": 10,
            }
        })

        step.followupSteps = []
        step.followupSteps.push(Stage.processEventSteps(step.omegastage, "GIFTED ITEM"))

        return step
    }
}

skills["dash"] = {
    "key": "dash",
    "name": "Dash",
    "role": "support",

    "type": "invoke",
    "range": 0,

    "getPriority": function() {
        return 1
    },
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        return performer.energy["move"] == false
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": {"x": -1},
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": performer.key,
                "energy": {"move": true},
            }
        })

        return step
    }
}

skills["rally"] = {
    "key": "rally",
    "name": "Rally",
    "role": "support",

    "description": "Rally: Can inspire an ally to act again.",

    "type": "invoke",
    "range": 1,

    "getPriority": getSupportPriority,
    "canTarget": function({performerKey, stage, position}) {
        const performer = stage.units[performerKey]
        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return false
        if(target.affiliation != performer.affiliation) return false
        if(target.energy["invoke skill"] == true && target.energy["move"] == true) return false
        return true
    },

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        Step.add(step, {
            "mark": 200,
            "unit": {
                "key": target.key,
                "energy": {"move": true, "invoke skill": true},
            }
        })
        Step.add(step, PlotFlourishes.NudgedMovement({
            "mark": 200,
            "unitKey": target.key,
            "position": target.position,
            "direction": direction,
        }))
        if(stage.flags["tutorial:hasRallied"] != true) {
            Step.add(step, {"mark": 200, ...Step.deltaflag("tutorial:hasRallied", true)})
        }

        return step
    }
}

skills["push"] = {
    "key": "push",
    "name": "Push",
    "role": "support",

    "description": "Push: Can push an ally 1 tile.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const vector = Geometry.vector(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        const previousPosition = position
        const nextPosition = Geometry.add(position, vector)

        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousPosition, nextPosition]
            },
        })

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 200,
                "unitKey": target.key,
                "startPosition": previousPosition,
                "endPosition": nextPosition,
            }))
            // Step.add(step, {
            //     "mark": 200,
            //     "unit": {
            //         "key": target.key,
            //         "position": nextPosition,
            //     }
            // })
        } else if(collision != undefined) {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 200,
                "unitKey": target.key,
                "position": target.position,
                "direction": direction,
            }))

            const damage = 1
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "health": target.health - damage
                },
            })

            if(collision.unit != undefined) {
                Step.add(step, {
                    "mark": 200,
                    "unit": {
                        "key": collision.unit.key,
                        "health": collision.unit.health - damage
                    },
                })
            }
        }

        return step
    }
}

skills["swap"] = {
    "key": "swap",
    "name": "Swap",
    "role": "support",

    "description": "Swap: Can swap places with an ally.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) {
            Step.add(step, [
                {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}}
            ])
            Step.add(step, PlotFlourishes.SwingBackMovement({
                "mark": 0,
                "unitKey": performer.key,
                "position": performer.position,
                "direction": direction,
            }))
            return step
        }

        Step.add(step, PlotFlourishes.PushedMovement({
            "mark": 0,
            "unitKey": performer.key,
            "startPosition": performer.position,
            "endPosition": target.position,
        }))
        Step.add(step, PlotFlourishes.PushedMovement({
            "mark": 0,
            "unitKey": target.key,
            "startPosition": target.position,
            "endPosition": performer.position,
        }))
        Step.add(step, [
            // {
            //     "mark": 0,
            //     "unit": {
            //         "key": performer.key,
            //         "position": target.position,
            //     }
            // },
            // {
            //     "mark": 0,
            //     "unit": {
            //         "key": target.key,
            //         "position": performer.position,
            //     }
            // },
            {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}}
        ])

        return step
    }
}

skills["pull"] = {
    "key": "pull",
    "name": "Pullback",
    "role": "support",

    "description": "Pullback: Can pull an ally back a tile.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)
        const oppositeDirection = Geometry.flip(direction)

        if(direction.x != 0
        && direction.y != 0) {
            console.log("Cannot calculate pull skill with non-orthogonal direction.")
            return step
        }

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) {
            return step
        }

        const previousPosition = performer.position
        const nextPosition = Geometry.add(performer.position, oppositeDirection)

        const previousTargetPosition = target.position
        const nextTargetPosition = Geometry.add(target.position, oppositeDirection)

        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousPosition, nextPosition]
            },
        })
        Step.add(step, {
            "intent": {
                "type": "reposition",
                "positions": [previousTargetPosition, nextTargetPosition]
            },
        })

        const collision = Stage.findCollision({
            "stage": stage,
            previousPosition,
            nextPosition,
        })

        if(collision == undefined) {
            // Step.add(step, {
            //     "mark": 0,
            //     "unit": {
            //         "key": performer.key,
            //         "position": nextPosition,
            //     },
            // })
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 0,
                "unitKey": performer.key,
                "startPosition": previousPosition,
                "endPosition": nextPosition,
            }))
            // Step.add(step, {
            //     "mark": 0,
            //     "unit": {
            //         "key": target.key,
            //         "position": nextTargetPosition,
            //     }
            // })
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 0,
                "unitKey": target.key,
                "startPosition": previousTargetPosition,
                "endPosition": nextTargetPosition,
            }))
            Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Pull"}})
        } else if(collision != undefined) {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 0,
                "unitKey": performer.key,
                "position": performer.position,
                "direction": direction,
            }))
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 0,
                "unitKey": target.key,
                "position": target.position,
                "direction": direction,
            }))
            Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        }

        return step
    }
}

skills["suplex"] = {
    "key": "suplex",
    "name": "Suplex",
    "role": "support",

    "description": "Suplex: Can flip an ally to your other side.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({"type": "invoke skill", "alphastage": stage})

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))
        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Pull"}})

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const previousPosition = performer.position
        const nextPosition = Geometry.add(performer.position, Geometry.flip(direction))

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            Step.add(step, {
                "mark": 200,
                "unit": {
                    "key": target.key,
                    "position": nextPosition,
                },
                "intent": {
                    "type": "reposition",
                    "positions": [target.position, performer.position, nextPosition]
                },
            })
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 200,
                "unitKey": target.key,
                "startPosition": target.position,
                "endPosition": nextPosition,
            }))
        } else {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 200,
                "unitKey": target.key,
                "position": target.position,
                "direction": direction,
            }))
        }

        return step
    }
}

skills["pivot"] = {
    "key": "pivot",
    "name": "Hop",
    "role": "support",

    "description": "Hop: Can hop over an ally.",

    "type": "invoke",
    "range": 1,
    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        const previousPosition = performer.position
        const nextPosition = Geometry.add(target.position, direction)

        const collision = Stage.findCollision({
            stage, previousPosition, nextPosition,
        })

        if(collision == undefined) {
            // Step.add(step, {
            //     "mark": 200,
            //     "unit": {
            //         "key": performer.key,
            //         "position": nextPosition,
            //     },
            //     "intent": {
            //         "type": "reposition",
            //         "positions": [performer.position, target.position, nextPosition]
            //     },
            // })
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 200,
                "unitKey": performer.key,
                "startPosition": performer.position,
                "endPosition": nextPosition,
            }))
        } else {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 200,
                "unitKey": performer.key,
                "position": performer.position,
                "direction": direction,
            }))
        }

        return step
    }
}

skills["smite"] = {
    "key": "smite",
    "name": "Push",
    "role": "support",

    "description": "Smite: Push an ally two tiles.",

    "type": "invoke",
    "range": 1,

    "canTarget": canTarget,
    "getPriority": getSupportPriority,

    "calculateStep": function({performerKey, stage, position}) {
        const step = Step.instantiate({
            "type": "invoke skill",
            "alphastage": stage
        })

        const performer = stage.units[performerKey]
        const direction = Geometry.direction(position, performer.position)

        Step.add(step, PlotFlourishes.SwingBackMovement({
            "mark": 0,
            "unitKey": performer.key,
            "position": performer.position,
            "direction": direction,
        }))

        Step.add(step, {"mark": 0, "audio": {"triggerKey": "Unit:Attack:Utility"}})
        Step.add(step, {"mark": 200, "audio": {"triggerKey": "Unit:Attack"}})

        const intendedDistance = 2
        const ray = Stage.findCollisionInDirection({
            "stage": stage,
            "position": position,
            "direction": direction,
            "maxdistance": intendedDistance,
        })

        Step.add(step, {
            "mark": 0,
            "intent": {
                "type": "reposition",
                "positions": ray.castedPositions
            }
        })

        const target = Stage.findUnitByPosition(stage, position)
        if(target == undefined) return step

        if(ray.distance > 0) {
            Step.add(step, PlotFlourishes.PushedMovement({
                "mark": 200,
                "unitKey": target.key,
                "startPosition": ray.startPosition,
                "endPosition": ray.endPosition,
            }))
        } else {
            Step.add(step, PlotFlourishes.NudgedMovement({
                "mark": 200,
                "unitKey": target.key,
                "position": target.position,
                "direction": direction,
            }))
        }

        // if(ray.distance < intendedDistance) {
        //     const damage = intendedDistance - ray.distance
        //     Step.add(step, {
        //         "mark": 200,
        //         "unit": {
        //             "key": target.key,
        //             "health": target.health - damage
        //         },
        //     })
        //
        //     if(ray?.collision?.unit != undefined) {
        //         Step.add(step, {
        //             "mark": 200,
        //             "unit": {
        //                 "key": ray.collision.unit.key,
        //                 "health": ray.collision.unit.health - damage
        //             },
        //         })
        //     }
        // }

        return step
    }
}

skills.get = function(skillKey) {
    return skills[skillKey]
}

export default skills
